import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { GetPatientResponse } from '../../../../services/patientCard';
import type { SubmissionFiles } from './submissionFiles';
import type { StepProgress } from './submissionProgress';
import { SUBMISSION_STEPS } from './submissionSteps';
import type { SubmissionFileAccess } from './useSubmissionFileAccess';
import { SubmissionStepTabs } from './SubmissionStepTabs';
import { PatientDetailsStep } from './PatientDetailsStep';
import { TreatmentStep } from './TreatmentStep';
import { PhotosStep } from './PhotosStep';

interface SubmissionProgressWizardProps {
    card: GetPatientResponse;
    files: SubmissionFiles;
    progress: readonly StepProgress[];
    access: SubmissionFileAccess;
}

export function SubmissionProgressWizard({
    card,
    files,
    progress,
    access
}: SubmissionProgressWizardProps) {
    const [stepIndex, setStepIndex] = useState(0);
    const lastIndex = SUBMISSION_STEPS.length - 1;
    const currentStep = SUBMISSION_STEPS[stepIndex];

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-center px-6 py-3 border-b border-gray-200 flex-shrink-0">
                <SubmissionStepTabs
                    steps={SUBMISSION_STEPS}
                    progress={progress}
                    stepIndex={stepIndex}
                    onSelect={setStepIndex}
                />
            </div>

            <div className="flex-1 min-h-0 overflow-auto bg-white px-6 py-5">
                {currentStep.id === 'patient' && <PatientDetailsStep card={card} />}
                {currentStep.id === 'treatment' && (
                    <TreatmentStep card={card} files={files} access={access} />
                )}
                {currentStep.id === 'photos' && (
                    <PhotosStep files={files} access={access} photosDate={card.dates?.photosDate} />
                )}
            </div>

            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 flex-shrink-0">
                <button
                    type="button"
                    onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                    disabled={stepIndex === 0}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Back
                </button>
                <span className="text-xs text-gray-500">
                    Step {stepIndex + 1} of {SUBMISSION_STEPS.length}
                </span>
                <button
                    type="button"
                    onClick={() => setStepIndex((i) => Math.min(lastIndex, i + 1))}
                    disabled={stepIndex === lastIndex}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Next
                    <ChevronRightIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
